import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ShareAltOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import { createStyles } from "antd-style";

import { useIsDemoExperience } from "@av/apps/common-apps/platform/services/demo-experience/demo";
import { JobPostDetail } from "@av/apps/recruitment/ats-common/components/jobs/JobPostDetail";
import { getLocalizedJobPostContent } from "@av/apps/recruitment/ats-common/localized-job-post-content";
import { jobsRoutes } from "@av/apps/recruitment/ats-common/routes/jobs/jobs.route-config";
import type { PublicJobPostDetailsModel } from "@av/apps/recruitment/ats-common/types";
import { isDevelopment } from "@av/build-constants";
import { Button } from "@av/components/atoms/Button";
import { Card } from "@av/components/atoms/Card";
import { Typography } from "@av/components/atoms/typography/Typography";
import {
  isSupportedLocale,
  type SupportedLocales,
} from "@av/components/organisms/editor/localized-text-editor/text-editor-supported-locales";
import { RenderDocAsHTML } from "@av/components/organisms/editor/rich-text-editor/RenderDocAsHTML";
import { useLocaleStore } from "@av/utils/hooks/useLocaleStore";
import { EmptyJobsState } from "./EmptyJobsState";
import { JobNotAvailable } from "./JobNotAvailable";

const { Title, Paragraph } = Typography;

const useStyles = createStyles(({ token, css }) => ({
  detailCard: css`
    box-shadow: ${token.boxShadowTertiary};

    @media (max-width: ${token.screenMD}px) {
      padding: ${token.paddingLG}px;
    }
  `,
  detailHeader: css`
    gap: ${token.marginSM}px;
    border-bottom: 1px solid ${token.colorSplit};
    padding: ${token.paddingSM}px ${token.paddingLG}px;
  `,
  shareLink: css`
    color: ${token.colorPrimary};
  `,
  emptyState: css`
    text-align: center;
    padding: ${token.paddingXL}px;
    color: ${token.colorTextSecondary};
  `,
}));

interface JobDetailProps {
  job: PublicJobPostDetailsModel | null;
  testIdPrefix: string;
  hasNoJobPostsMatchingFilters: boolean;
  clearFilters: () => void;
}

export function JobDetailSection({
  job,
  testIdPrefix,
  clearFilters,
  hasNoJobPostsMatchingFilters,
}: JobDetailProps) {
  const { styles } = useStyles();
  const { t } = useTranslation();
  const { locale } = useLocaleStore();
  const descriptionLocale: SupportedLocales = isSupportedLocale(locale) ? locale : "en-CA";
  const navigate = useNavigate();
  const isDemoExperience = useIsDemoExperience();

  if (!job) {
    return hasNoJobPostsMatchingFilters ? (
      <EmptyJobsState onClearFiltersApplied={clearFilters} />
    ) : (
      <Card testIdPrefix="job-details-empty" styles={{ body: { padding: 0 } }}>
        <div className={styles.emptyState}>
          <Paragraph testIdPrefix={`${testIdPrefix}-detail-empty`}>
            {t("recruitment.jobs.selectAJobToSeeDetailsMessage")}
          </Paragraph>
        </div>
      </Card>
    );
  }

  if (job.postStatus === "CLOSED" || job.postStatus === "ARCHIVED") {
    return <JobNotAvailable jobPostId={job.jobPostId} />;
  }

  return (
    <Card
      testIdPrefix="job-details"
      className={styles.detailCard}
      styles={{ body: { padding: 0 } }}
    >
      <Flex align="center" justify="space-between" className={styles.detailHeader}>
        <Title level={5} testIdPrefix={`${testIdPrefix}-detail-label`}>
          {t("recruitment.jobs.jobDetails_title")}
        </Title>
        <Flex align="center" gap={10}>
          {isDemoExperience && (
            <Button
              icon={<ShareAltOutlined className={styles.shareLink} />}
              color="primary"
              variant="text"
              testIdPrefix={`${testIdPrefix}-detail-share`}
            />
          )}
          <Button
            type="primary"
            testIdPrefix={`${testIdPrefix}-detail-apply`}
            onClick={() => navigate(jobsRoutes.home.apply.path({ pathParam: job.jobPostId }))}
          >
            {t("recruitment.jobs.apply")}
          </Button>
        </Flex>
      </Flex>
      <JobPostDetail
        isPublic
        jobPost={job}
        testIdPrefix={testIdPrefix}
        jobPostContent={
          <Flex vertical gap={12}>
            <Title level={4} testIdPrefix={`${testIdPrefix}-description`}>
              {t("recruitment.jobs.addJob_description_title")}
            </Title>
            <RenderDocAsHTML
              testIdPrefix={testIdPrefix}
              doc={getLocalizedJobPostContent(job.descriptions, descriptionLocale)}
              isDevelopment={isDevelopment}
            />
          </Flex>
        }
      />
    </Card>
  );
}
